import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ShieldOff, ShieldCheck, Loader2 } from 'lucide-react'

const STATUS_COLORS: Record<string, string> = {
  active:    'bg-green-100 text-green-700',
  pending:   'bg-amber-100 text-amber-700',
  rejected:  'bg-red-100 text-red-700',
  revoked:   'bg-gray-100 text-gray-500',
  suspended: 'bg-orange-100 text-orange-700',
}

interface Props {
  orgId: string
  canEdit: boolean
}

export function RestrictionsTab({ orgId, canEdit }: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [updating, setUpdating] = useState<string | null>(null)

  const { data: accreditations, isLoading } = useQuery({
    queryKey: ['accreditation-restrictions', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_accreditations' as any)
        .select(`
          id, status, journalist_id, accredited_at, updated_at,
          journalist_profiles(full_name)
        `)
        .eq('org_id', orgId)
        .in('status', ['active', 'suspended', 'revoked'])
        .order('updated_at', { ascending: false }) as any)
      return (data ?? []) as any[]
    },
  })

  const setStatus = async (id: string, status: 'active' | 'suspended') => {
    setUpdating(id)
    try {
      await (supabase
        .from('journalist_accreditations' as any)
        .update({ status })
        .eq('id', id) as any)
      toast({ title: status === 'suspended' ? 'Access restricted.' : 'Access restored.' })
      qc.invalidateQueries({ queryKey: ['accreditation-restrictions', orgId] })
    } catch {
      toast({ title: 'Failed to update status.', variant: 'destructive' })
    } finally {
      setUpdating(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const active = (accreditations ?? []).filter((a: any) => a.status === 'active')
  const restricted = (accreditations ?? []).filter((a: any) => a.status === 'suspended' || a.status === 'revoked')

  return (
    <div className="space-y-6">
      <p className="text-justify text-sm text-muted-foreground">
        Suspend portal journalists from submitting content on behalf of your organisation.
        Suspended journalists cannot access the portal for this organisation until access is restored.
      </p>

      {/* Active journalists */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Accredited Journalists ({active.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {active.length === 0 ? (
            <p className="text-justify px-4 py-6 text-sm text-muted-foreground text-center">No active accreditations.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Journalist</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Accredited</th>
                  <th className="px-4 py-2.5 text-center font-medium text-muted-foreground w-24">Status</th>
                  {canEdit && <th className="px-4 py-2.5 w-32" />}
                </tr>
              </thead>
              <tbody>
                {active.map((acc: any) => (
                  <tr key={acc.id} className="border-b last:border-0 hover:bg-muted/10">
                    <td className="px-4 py-3 font-medium">
                      {(acc.journalist_profiles as any)?.full_name ?? acc.journalist_id.slice(0, 8) + '…'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {acc.accredited_at ? new Date(acc.accredited_at).toLocaleDateString() : ''}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={`text-[10px] ${STATUS_COLORS[acc.status] ?? ''}`}>
                        {acc.status}
                      </Badge>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                          disabled={updating === acc.id}
                          onClick={() => setStatus(acc.id, 'suspended')}
                        >
                          {updating === acc.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <ShieldOff className="w-3 h-3" />}
                          Restrict
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Restricted / Revoked */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Restricted / Revoked ({restricted.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {restricted.length === 0 ? (
            <p className="text-justify px-4 py-6 text-sm text-muted-foreground text-center">No restricted journalists.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Journalist</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Updated</th>
                  <th className="px-4 py-2.5 text-center font-medium text-muted-foreground w-24">Status</th>
                  {canEdit && <th className="px-4 py-2.5 w-36" />}
                </tr>
              </thead>
              <tbody>
                {restricted.map((acc: any) => (
                  <tr key={acc.id} className="border-b last:border-0 hover:bg-muted/10">
                    <td className="px-4 py-3 font-medium">
                      {(acc.journalist_profiles as any)?.full_name ?? acc.journalist_id.slice(0, 8) + '…'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {acc.updated_at ? new Date(acc.updated_at).toLocaleDateString() : ''}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={`text-[10px] ${STATUS_COLORS[acc.status] ?? ''}`}>
                        {acc.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canEdit && acc.status === 'suspended' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1 text-green-600 border-green-200 hover:bg-green-50"
                          disabled={updating === acc.id}
                          onClick={() => setStatus(acc.id, 'active')}
                        >
                          {updating === acc.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <ShieldCheck className="w-3 h-3" />}
                          Restore
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">Revoked via press pass</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
